import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import InlineAlert from "src/grading/components/InlineAlert/InlineAlert";
import LoadingButton from "src/grading/components/LoadingButton/LoadingButton";
import EntryFrame from "src/grading/layout/EntryFrame";
import { SIGNED_IN_PATH } from "src/grading/pages/Login/Login";
import { apiSlice } from "src/store/apiSlice";
import { useAppDispatch } from "src/store/hooks";
import {
  useLazyGetPermissionsQuery,
  useMarkMfaEnrolledMutation,
} from "src/store/reducers/auth/authApiSlice";
import { setPermissions } from "src/store/reducers/auth/authSlice";

const hostedUi = process.env.REACT_APP_COGNITO_HOSTED_UI_DOMAIN;
const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;

/**
 * Administrators without MFA land here (403 MFA_REQUIRED). TOTP enrollment happens in the Cognito
 * hosted UI; afterwards the app confirms with `PUT /me/mfa/enrolled`, which checks the token's
 * `amr` claim. Districts whose provider enforces MFA attest instead and never see this page.
 */
const MfaEnrollPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const [markEnrolled, { isLoading }] = useMarkMfaEnrolledMutation();
  const [getPermissions] = useLazyGetPermissionsQuery();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = `${t("auth.mfaTitle", { defaultValue: "Set up two-step verification" })} · ${t("app.name")}`;
  }, [t]);

  const openHostedUi = () => {
    if (!hostedUi || !clientId) {
      setError(
        "Two-step verification is managed by your district's sign-in provider. Ask your administrator to record the MFA attestation for your district.",
      );
      return;
    }
    const base = hostedUi.startsWith("http") ? hostedUi : `https://${hostedUi}`;
    const redirect = encodeURIComponent(
      `${window.location.origin}/auth/callback`,
    );
    window.location.href = `${base}/login?client_id=${clientId}&response_type=code&scope=openid+email+profile&redirect_uri=${redirect}`;
  };

  const confirm = async () => {
    setError(null);
    try {
      await markEnrolled().unwrap();
      const permissions = await getPermissions().unwrap();
      dispatch(
        setPermissions({
          permissions: permissions.permissions,
          scopes: permissions.scopes,
          mfaRequired: permissions.mfaRequired,
          license: permissions.license,
        }),
      );
      dispatch(apiSlice.util.resetApiState());
      history.push(SIGNED_IN_PATH);
    } catch (err: unknown) {
      const message =
        typeof err === "object" && err !== null
          ? (err as { data?: { message?: string } }).data?.message
          : undefined;
      setError(
        message ??
          t("auth.mfaNotYet", {
            defaultValue:
              "Your sign-in does not yet show two-step verification. Finish enrollment, sign in again, then confirm.",
          }),
      );
    }
  };

  return (
    <EntryFrame
      eyebrow={t("auth.twoStep", { defaultValue: "Two-step verification" })}
      title={t("auth.mfaTitle", {
        defaultValue: "Set up two-step verification",
      })}
    >
      <Stack spacing={2.5}>
        <Typography variant="body2" color="text.secondary">
          {t("auth.mfaBody", {
            defaultValue:
              "Administrator accounts protect student records with a second factor. Enroll an authenticator app, sign in again, then confirm below.",
          })}
        </Typography>
        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={openHostedUi}
        >
          {t("auth.mfaEnroll", { defaultValue: "Enroll an authenticator app" })}
        </Button>
        <LoadingButton
          variant="outlined"
          size="large"
          fullWidth
          label={t("auth.mfaEnrolled", { defaultValue: "I have enrolled" })}
          loading={isLoading}
          disabled={isLoading}
          onClick={confirm}
        />
        {error ? <InlineAlert severity="warning">{error}</InlineAlert> : null}
      </Stack>
    </EntryFrame>
  );
};

export default MfaEnrollPage;
